// FILE: src/Components/BrandKit/index.js (FULLY UPDATED)

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { message, Spin, Switch, Button, Input, Select, Modal, Popover, Tag, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { SketchPicker } from 'react-color';
import WebFont from 'webfontloader';

import { getProfileDetails, updateOnboardingData } from '../../services/group';
import { uploadOnboardingAsset, getGoogleFonts } from '../../services/assets';
import CountryDropdown from '../../Comman Components/CountryDropdown';
import { country as countryList } from '../../Comman Components/Country';
import { TimeZone } from '../../Comman Components/TimeZone';
import TimezoneDropdown from '../../Comman Components/TimezoneDropdown';
import axiosServices from '../../services/axios';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';


const { Option } = Select;
const { TextArea } = Input;

// --- Reusable UI Components ---

const SectionCard = ({ title, subtitle, children }) => (
<div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
<div className="p-6 border-b border-gray-200">
<h3 className="text-xl font-semibold text-gray-800">{title}</h3>
{subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
</div>
<div className="p-6 space-y-5">
{children}
</div>
</div>
);

const EditableField = ({ label, value, onSave, isSaving, children, type = 'text' }) => {
const [isEditing, setIsEditing] = useState(false);
const [currentValue, setCurrentValue] = useState(value);

useEffect(() => {
if (!isEditing) {
setCurrentValue(value);
}
}, [value, isEditing]);

const handleToggleEdit = () => {
if (!isEditing) {
setCurrentValue(value);
}
setIsEditing(!isEditing);
};

const handleSave = () => {
if (typeof onSave === 'function') {
onSave(currentValue);
}
setIsEditing(false);
};

const handleChildChange = (eventOrValue) => {
const newValue = eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
setCurrentValue(newValue);
};

const displayValue = value || '-';

return (
<div className="py-3 border-b border-gray-100 last:border-b-0">
{isEditing ? (
<div>
<label className="block text-sm font-semibold text-gray-600 mb-2">{label}</label>
{children ? (
React.cloneElement(children, {
value: currentValue,
onChange: handleChildChange
})
) : type === 'textarea' ? (
<TextArea rows={4} value={currentValue} onChange={(e) => setCurrentValue(e.target.value)} />
) : (
<Input value={currentValue} onChange={(e) => setCurrentValue(e.target.value)} onPressEnter={handleSave} />
)}
<div className="flex justify-end gap-2 mt-2">
<Button size="small" onClick={handleToggleEdit}>Cancel</Button>
<Button size="small" type="primary" onClick={handleSave} loading={isSaving}>Save</Button>
</div>
</div>
) : (
<div className="flex justify-between items-start">
<label className="block text-sm font-semibold text-gray-600 pt-1">{label}</label>
<div className="flex items-center gap-2">
<p className="text-gray-800 text-right max-w-md break-words whitespace-pre-wrap">{displayValue}</p>
<Button type="text" shape="circle" icon={<i className="fa fa-pencil text-gray-400 hover:text-blue-500"></i>} onClick={handleToggleEdit} />
</div>
</div>
)}
</div>
);
};

const EditableTagList = ({ label, items = [], fieldKey, onSave }) => {
const [inputValue, setInputValue] = useState('');
const [inputVisible, setInputVisible] = useState(false);
const inputRef = useRef(null);

useEffect(() => {
if (inputVisible) {
inputRef.current?.focus();
}
}, [inputVisible]);

const handleSaveList = (newList) => {
onSave(fieldKey, newList);
};

const handleClose = (removedTag) => {
const newList = items.filter((tag) => tag !== removedTag);
handleSaveList(newList);
};

const showInput = () => {
setInputVisible(true);
};

const handleInputChange = (e) => {
setInputValue(e.target.value);
};

const handleInputConfirm = () => {
if (inputValue && !items.includes(inputValue)) {
const newList = [...items, inputValue];
handleSaveList(newList);
}
setInputVisible(false);
setInputValue('');
};

return (
<div className="py-3">
<label className="block text-sm font-semibold text-gray-600 mb-2">{label}</label>
<div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[50px]">
{items.map((item, index) => (
<Tag
key={`${item}-${index}`}
closable
onClose={(e) => {
e.preventDefault();
handleClose(item);
}}
className="px-3 py-1.5 text-sm rounded-md m-0 border border-gray-200 bg-white text-gray-700 flex items-center gap-1 shadow-sm"
>
{item}
</Tag>
))}
{inputVisible ? (
<Input
ref={inputRef}
type="text"
size="small"
style={{ width: 120, borderRadius: 6 }}
value={inputValue}
onChange={handleInputChange}
onBlur={handleInputConfirm}
onPressEnter={handleInputConfirm}
/>
) : (
<Tag
onClick={showInput}
className="cursor-pointer border-dashed border-gray-400 bg-white hover:border-blue-500 hover:text-blue-500 rounded-md px-3 py-1.5 flex items-center gap-1 transition-colors"
>
<PlusOutlined /> Add
</Tag>
)}
</div>
</div>
);
};

const SegmentedControl = ({ label, options, value, fieldKey, onSave }) => (
<div className="py-3 border-b border-gray-100 last:border-b-0">
<div className="flex justify-between items-center">
<label className="block text-sm font-semibold text-gray-600">{label}</label>
<div className="flex rounded-md border border-gray-300 p-0.5 bg-gray-100">
{options.map(option => (
<button
key={option}
onClick={() => onSave(fieldKey, option)}
className={`w-full px-4 py-1 text-sm rounded transition-all duration-200 ${value === option ? 'bg-white shadow text-blue-600 font-semibold' : 'text-gray-600 hover:bg-gray-200'}`}
>
{option}
</button>
))}
</div>
</div>
</div>
);


// --- Main BrandKit Component ---
export default function BrandKit() {
const [profileData, setProfileData] = useState(null);
const [isLoading, setIsLoading] = useState(true);
const [isSaving, setIsSaving] = useState(false);

const { selectedGroup, groups } = useSelector((state) => state.groups);

const fetchData = useCallback(async () => {
if (!selectedGroup || !groups || groups.length === 0) return;

const currentGroup = groups.find(g => g.name === selectedGroup);
if (!currentGroup?._id) {
setIsLoading(false);
return;
}
const groupId = currentGroup._id;

setIsLoading(true);
try {
const data = await getProfileDetails(groupId);
const profile = data.OnBoarding?.[0] || null;
setProfileData(profile);
} catch (error) {
console.error('[BrandKit Fetch Error]:', error);
message.error("Failed to fetch brand data.");
setProfileData(null);
} finally {
setIsLoading(false);
}
}, [selectedGroup, groups]);

useEffect(() => {
if (selectedGroup) {
fetchData();
}
}, [selectedGroup, fetchData]);

const handleSaveField = async (fieldKey, value) => {
const originalData = profileData;
// Optimistically update the UI immediately
setProfileData(prev => ({ ...prev, [fieldKey]: value }));

setIsSaving(true);
try {
const payload = { ...originalData, [fieldKey]: value, groupId: originalData.groupId };
const response = await updateOnboardingData(payload);
// Sync with the server's response just in case
setProfileData(response.data);
message.success(`${fieldKey.replace(/_/g, ' ')} updated!`);
} catch (error) {
message.error(`Failed to update ${fieldKey}. Reverting changes.`);
setProfileData(originalData); // Revert UI on error
} finally {
setIsSaving(false);
}
};

if (isLoading) {
return <div className="flex justify-center items-center h-screen"><Spin size="large" /></div>;
}

if (!profileData) {
return (
<div className="flex justify-center items-center h-screen text-center text-gray-500">
<div>
<h2 className="text-xl font-semibold">No Brand Kit Found</h2>
<p>There is no brand kit associated with the group "{selectedGroup}".</p>
</div>
</div>
);
}

return (
<div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
<header className="mb-8 max-w-5xl mx-auto">
<h1 className="text-3xl font-bold text-gray-900">Brand Kit</h1>
<p className="text-gray-600 mt-1">Manage your brand's identity, visuals, and AI settings all in one place.</p>
</header>

<div className="max-w-5xl mx-auto">
<BusinessProfilePanel profileData={profileData} onSaveField={handleSaveField} isSaving={isSaving} />
<VisualsPanel profileData={profileData} onSaveField={handleSaveField} />
<WritingStylePanel profileData={profileData} onSaveField={handleSaveField} isSaving={isSaving} />
<CustomerPositioningPanel profileData={profileData} onSaveField={handleSaveField} />
<ContentTopicsPanel profileData={profileData} setProfileData={setProfileData} groupId={profileData.groupId} />
</div>
</div>
);
}

// --- Section-Specific Components ---

const BusinessProfilePanel = ({ onSaveField, isSaving, profileData }) => (
<SectionCard title="Business Profile" subtitle="Core facts about your brand.">
<EditableField label="Brand Name" value={profileData?.brand_name} onSave={(newValue) => onSaveField('brand_name', newValue)} isSaving={isSaving} />
<EditableField label="Website" value={profileData?.website} onSave={(newValue) => onSaveField('website', newValue)} isSaving={isSaving} />
<EditableField label="Business Overview" value={profileData?.describes} onSave={(newValue) => onSaveField('describes', newValue)} isSaving={isSaving} type="textarea" />
<EditableField label="Country" value={profileData?.country} onSave={(newValue) => onSaveField('country', newValue)} isSaving={isSaving}>
<CountryDropdown countryList={countryList} />
</EditableField>
<EditableField label="Time Zone" value={profileData?.time_zone} onSave={(newValue) => onSaveField('time_zone', newValue)} isSaving={isSaving}>
<TimezoneDropdown Timezonelist={TimeZone} />
</EditableField>
</SectionCard>
);

const VisualsPanel = ({ profileData, onSaveField }) => {
const userProfile = useSelector(state => state.Profiles[0]);
const logoInputRef = useRef(null);

const handleFileUpload = async (e) => {
const file = e.target.files[0];
if (!file) return;

const brandName = profileData?.brand_name || userProfile?.first_name || "default-brand";
try {
const response = await uploadOnboardingAsset(file, brandName);
const newLogos = [...(profileData.website_image_url || []), response.url];
onSaveField('website_image_url', newLogos);
message.success("Logo uploaded!");
} catch (error) {
message.error("Upload failed.");
}
};

const handleDeleteLogo = (logoUrl) => {
const newLogos = (profileData.website_image_url || []).filter(url => url !== logoUrl);
onSaveField('website_image_url', newLogos);
};

return (
<SectionCard title="Visual Identity" subtitle="Manage your logos, colors, fonts, and AI visual styles.">
<div className="space-y-6">
<div>
<h4 className='font-semibold text-gray-700 mb-2'>Logos</h4>
<div className='flex flex-wrap items-center gap-4'>
{(profileData?.website_image_url || []).map(url => (
<div key={url} className="relative brandkit-logo-container w-24 h-24 p-2 border rounded-lg bg-gray-50 flex items-center justify-center">
<img src={url} alt="logo" className="max-w-full max-h-full object-contain" />
<button onClick={() => handleDeleteLogo(url)} className="absolute top-1 right-1 bg-white text-red-500 rounded-full w-5 h-5 flex items-center justify-center shadow-md brandkit-delete-btn">
<CloseOutlined style={{ fontSize: '10px' }}/>
</button>
</div>
))}
<button onClick={() => logoInputRef.current.click()} className="w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-blue-500 hover:text-blue-500">
<PlusOutlined className="text-2xl"/>
<span className="text-xs font-semibold mt-1">Upload</span>
</button>
<input type="file" ref={logoInputRef} onChange={handleFileUpload} className="hidden" accept="image/*" />
</div>
</div>

<div>
<h4 className='font-semibold text-gray-700 mb-2'>Brand Palette</h4>
<PaletteColorSection palette={profileData?.palette} onSave={(newPalette) => onSaveField('palette', newPalette)} />
</div>

<div>
<h4 className='font-semibold text-gray-700 mb-2'>Typography</h4>
<div className="grid sm:grid-cols-2 gap-4">
<FontSelector label="Header Font" selectedFont={profileData?.header_font} onSelectFont={font => onSaveField('header_font', font)} />
<FontSelector label="Body Font" selectedFont={profileData?.body_font} onSelectFont={font => onSaveField('body_font', font)} />
</div>
</div>
<div>
    <h4 className='font-semibold text-gray-700 mb-2'>AI Visual Styles</h4>
    <VisualStylesPanel styles={profileData?.Visual_Style_Guides} onSave={(newStyles) => onSaveField('Visual_Style_Guides', newStyles)} />
</div>
</div>
</SectionCard>
);
};

const WritingStylePanel = ({ onSaveField, isSaving, profileData }) => (
<SectionCard title="Writing Style & Tone" subtitle="Control how the AI writes your content.">
<EditableField label="Tone of Voice" value={profileData?.tone} onSave={(newValue) => onSaveField('tone', newValue)} isSaving={isSaving} type="textarea" />
<EditableField label="Language" value={profileData?.language} onSave={(newValue) => onSaveField('language', newValue)} isSaving={isSaving}>
<Select className="w-full">
{["English", "Spanish", "French", "German", "Mandarin", "Hindi", "Arabic", "Portuguese", "Russian", "Japanese"].map(lang =>
<Option key={lang} value={lang}>{lang}</Option>
)}
</Select>
</EditableField>
<SegmentedControl label="Caption Sentence Length" options={['Short', 'Mid', 'Long']} value={profileData?.caption_sentence_length} fieldKey="caption_sentence_length" onSave={onSaveField} />
<SegmentedControl label="Emoji Level" options={['Low', 'Mid', 'High']} value={profileData?.emoji_level} fieldKey="emoji_level" onSave={onSaveField} />
<SegmentedControl label="Self-Promotional Level" options={['Low', 'Mid', 'High']} value={profileData?.self_promotional_level} fieldKey="self_promotional_level" onSave={onSaveField} />
<EditableTagList label="Writing Samples" items={profileData?.writing_sample} fieldKey="writing_sample" onSave={onSaveField} />
</SectionCard>
);

const CustomerPositioningPanel = ({ profileData, onSaveField }) => (
<SectionCard title="Customer & Positioning" subtitle="Give the AI context about your business and audience.">
<div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
<div>
<EditableTagList label="My Niche" items={profileData?.my_niche} fieldKey="my_niche" onSave={onSaveField} />
<EditableTagList label="Target Audience" items={profileData?.audience} fieldKey="audience" onSave={onSaveField} />
<EditableTagList label="Pain Points" items={profileData?.pain_points} fieldKey="pain_points" onSave={onSaveField} />
</div>
<div>
<EditableTagList label="Core Values" items={profileData?.core_values} fieldKey="core_values" onSave={onSaveField} />
<EditableTagList label="Objectives" items={profileData?.objectives} fieldKey="objectives" onSave={onSaveField} />
<EditableTagList label="Call to Actions (CTAs)" items={profileData?.CTAs} fieldKey="CTAs" onSave={onSaveField} />
<EditableTagList label="Hashtags" items={profileData?.hashtags} fieldKey="hashtags" onSave={onSaveField} />
</div>
</div>
</SectionCard>
);

const ContentTopicsPanel = ({ profileData, setProfileData, groupId }) => {
const [addTopicText, setAddTopicText] = useState("");
const [editingIndex, setEditingIndex] = useState(null);
const [tempTopicText, setTempTopicText] = useState("");

const handleOptimisticUpdate = (newContent) => {
setProfileData(prev => ({...prev, content: newContent }));
};

const addTopic = async () => {
if (!addTopicText.trim()) return;
const newTopic = { text: addTopicText, enabled: true };
const newContent = [...(profileData.content || []), newTopic];
handleOptimisticUpdate(newContent);
setAddTopicText('');

try {
await axiosServices.patch(`/onboarding/update-content`, { text: addTopicText, enabled: true, groupId });
} catch (err) {
message.error("Failed to add topic.");
setProfileData(profileData); // Revert
}
};

const updateTopic = async (index, text, enabled) => {
const newContent = profileData.content.map((item, i) => i === index ? { ...item, text, enabled } : item);
handleOptimisticUpdate(newContent);
setEditingIndex(null);

try {
await axiosServices.patch(`/onboarding/update-content/${index}`, { text, enabled, groupId });
} catch (err) {
message.error("Failed to update topic.");
setProfileData(profileData);
}
};

const deleteTopic = (index) => {
const newContent = profileData.content.filter((_, i) => i !== index);
handleOptimisticUpdate(newContent);

Modal.confirm({
title: "Are you sure you want to delete this topic?",
onOk: async () => {
try {
await axiosServices.post(`/onboarding/delete-content/${index}`, { groupId });
} catch (err) {
message.error("Failed to delete topic.");
setProfileData(profileData);
}
},
onCancel: () => {
setProfileData(profileData); // Revert if cancelled
}
});
};

const handleSaveEdit = (index) => {
updateTopic(index, tempTopicText, profileData.content[index].enabled);
};

return (
<SectionCard title="Content Topics" subtitle="Enable or disable topics for the AI to generate posts about.">
<div className="space-y-2">
{profileData?.content?.map((topic, index) => (
<div key={index} className="flex items-center gap-3 bg-gray-50 p-2 rounded-md shadow-sm">
<Switch checked={topic.enabled} onChange={(checked) => updateTopic(index, topic.text, checked)} />
{editingIndex === index ? (
<Input value={tempTopicText} onChange={(e) => setTempTopicText(e.target.value)} onPressEnter={() => handleSaveEdit(index)} />
) : (
<p className="flex-grow text-sm">{topic.text}</p>
)}
<div className="flex items-center">
{editingIndex === index ? (
<>
<Button size="small" type="primary" onClick={() => handleSaveEdit(index)}>Save</Button>
<Button size="small" className="ml-1" onClick={() => setEditingIndex(null)}>Cancel</Button>
</>
) : (
<Button type="text" shape="circle" icon={<i className="fa fa-pencil text-gray-400"></i>} onClick={() => { setEditingIndex(index); setTempTopicText(topic.text); }} />
)}
<Button type="text" shape="circle" danger icon={<i className="fa fa-trash text-red-400"></i>} onClick={() => deleteTopic(index)} />
</div>
</div>
))}
</div>
<div className="flex items-center gap-2 mt-4">
<Input value={addTopicText} onChange={(e) => setAddTopicText(e.target.value)} placeholder="Add a new content topic" />
<Button type="primary" onClick={addTopic}>Add Topic</Button>
</div>
</SectionCard>
);
};

const PaletteColorSection = ({ palette, onSave }) => {
const [isPickerOpen, setIsPickerOpen] = useState(false);
const [newColor, setNewColor] = useState('#000000');
const colors = palette ? palette.split(',').filter(Boolean) : [];

const handleAddColor = () => {
const newPalette = [...colors, newColor].join(',');
onSave(newPalette);
setIsPickerOpen(false);
};

const handleDeleteColor = (colorToDelete) => {
const newPalette = colors.filter(c => c !== colorToDelete).join(',');
onSave(newPalette);
};

return (
<div className="flex flex-wrap gap-4 items-center">
{colors.map((color, i) => (
<div key={i} className="relative brandkit-color-container w-10 h-10">
<div className="w-10 h-10 rounded-full cursor-pointer shadow-sm border-2 border-gray-200" style={{ backgroundColor: color }} title={color}></div>
<button onClick={() => handleDeleteColor(color)} className="absolute top-0 right-0 bg-white text-red-500 rounded-full w-5 h-5 flex items-center justify-center shadow-md brandkit-delete-btn">
<CloseOutlined style={{ fontSize: '10px' }} />
</button>
</div>
))}
<Popover
content={
<div className="text-center">
<SketchPicker color={newColor} onChange={(c) => setNewColor(c.hex)} disableAlpha />
<Button type="primary" size="small" className="mt-3 w-full" onClick={handleAddColor}>Add Color</Button>
</div>
}
title="Add New Color" trigger="click" open={isPickerOpen} onOpenChange={setIsPickerOpen} >
<div className="w-10 h-10 rounded-full border-2 border-dashed flex items-center justify-center cursor-pointer text-gray-400 hover:border-blue-500 hover:text-blue-500">
<PlusOutlined />
</div>
</Popover>
</div>
);
};

const FontSelector = ({ label, selectedFont, onSelectFont }) => {
const [isOpen, setIsOpen] = useState(false);
const [fonts, setFonts] = useState([]);
const popoverRef = useRef(null);

useEffect(() => {
getGoogleFonts(setFonts, () => {}, () => {});
}, []);

useEffect(() => {
if (selectedFont) WebFont.load({ google: { families: [selectedFont] } });
}, [selectedFont]);

useEffect(() => {
const handleClickOutside = (event) => {
if (popoverRef.current && !popoverRef.current.contains(event.target)) {
setIsOpen(false);
}
};
if (isOpen) {
document.addEventListener("mousedown", handleClickOutside);
}
return () => {
document.removeEventListener("mousedown", handleClickOutside);
};
}, [isOpen]);

return (
<div className="relative" ref={popoverRef}>
<label className="block text-sm font-semibold text-gray-600 mb-1">{label}</label>
<Button onClick={() => setIsOpen(!isOpen)} className="w-full text-left flex justify-between items-center">
<span style={{fontFamily: selectedFont}}>{selectedFont || 'Select Font'}</span>
<i className={`fa fa-chevron-down text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
</Button>
{isOpen && (
<ul className="absolute z-20 w-full bg-white border max-h-48 overflow-y-auto rounded-md shadow-lg mt-1 animate-fade-in-up">
{fonts?.map(font => (
<li key={font} style={{fontFamily: font}} className="p-2 hover:bg-gray-100 cursor-pointer" onClick={() => { onSelectFont(font); setIsOpen(false); }}>
{font}
</li>
))}
</ul>
)}
</div>
);
};

// --- START: NEW COMPONENT FOR VISUAL STYLES ---
// --- START: NEW COMPONENT FOR VISUAL STYLES ---
const VisualStylesPanel = ({ styles, onSave }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newStyle, setNewStyle] = useState({ key: '', value: '' });
    
    // State for editing: now tracks both the original key (to find the item) and the new values
    const [editingOriginalKey, setEditingOriginalKey] = useState(null);
    const [editingState, setEditingState] = useState({ key: '', value: '' });

    const stylesObject = useMemo(() => (typeof styles === 'object' && styles !== null ? styles : {}), [styles]);

    const handleDeleteStyle = (keyToDelete) => {
        Modal.confirm({
            title: `Delete '${keyToDelete.replace(/_/g, ' ')}'?`,
            content: "Are you sure you want to delete this visual style?",
            okText: "Delete",
            okType: "danger",
            onOk: () => {
                const newStyles = { ...stylesObject };
                delete newStyles[keyToDelete];
                onSave(newStyles);
            }
        });
    };

    const handleAddStyle = () => {
        if (!newStyle.key.trim() || !newStyle.value.trim()) {
            message.error("Style name and description are required.");
            return;
        }
        const newKey = newStyle.key.trim().replace(/\s+/g, '_');
        if (stylesObject.hasOwnProperty(newKey)) {
            message.error("A style with this name already exists.");
            return;
        }
        const newStyles = { ...stylesObject, [newKey]: newStyle.value };
        onSave(newStyles);
        setIsAdding(false);
        setNewStyle({ key: '', value: '' });
    };

    const handleStartEdit = (key, value) => {
        setEditingOriginalKey(key);
        // Pre-fill the editing state, replacing underscores with spaces for readability in the input
        setEditingState({ key: key.replace(/_/g, ' '), value });
    };

    const handleCancelEdit = () => {
        setEditingOriginalKey(null);
        setEditingState({ key: '', value: '' });
    };
    
    const handleSaveEdit = () => {
        if (!editingState.key.trim() || !editingState.value.trim()) {
            message.error("Style name and description cannot be empty.");
            return;
        }

        const newKeyFormatted = editingState.key.trim().replace(/\s+/g, '_');
        
        // Check for duplicate key name (unless it's the same key we're editing)
        if (newKeyFormatted !== editingOriginalKey && stylesObject.hasOwnProperty(newKeyFormatted)) {
             message.error("A style with this name already exists.");
             return;
        }

        const newStyles = { ...stylesObject };
        
        // If the key name changed, delete the old key
        if (newKeyFormatted !== editingOriginalKey) {
            delete newStyles[editingOriginalKey];
        }
        
        // Set the new/updated key-value pair
        newStyles[newKeyFormatted] = editingState.value;
        
        onSave(newStyles);
        setEditingOriginalKey(null);
        setEditingState({ key: '', value: '' });
    };

    return (
        <div className="space-y-3">
            {Object.entries(stylesObject).map(([key, value]) => (
                <div key={key} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    {editingOriginalKey === key ? (
                        <div className="space-y-3">
                             <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Style Name</label>
                                <Input 
                                    value={editingState.key} 
                                    onChange={(e) => setEditingState(p => ({ ...p, key: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Description</label>
                                <TextArea 
                                    rows={4} 
                                    value={editingState.value} 
                                    onChange={(e) => setEditingState(p => ({ ...p, value: e.target.value }))}
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button size="small" onClick={handleCancelEdit}>Cancel</Button>
                                <Button size="small" type="primary" onClick={handleSaveEdit}>Save</Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-grow min-w-0">
                                <h5 className="font-semibold text-sm text-slate-700 capitalize truncate">{key.replace(/_/g, ' ')}</h5>
                                <p className="text-sm text-slate-600 mt-1 truncate">{value}</p>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-1">
                                <Tooltip title="Edit">
                                    <Button type="text" shape="circle" icon={<i className="fa-solid fa-pencil text-slate-500"></i>} onClick={() => handleStartEdit(key, value)} />
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <Button type="text" shape="circle" danger icon={<i className="fa-solid fa-trash-can"></i>} onClick={() => handleDeleteStyle(key)} />
                                </Tooltip>
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {isAdding && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3 animate-fade-in">
                    <div>
                        <label className="block text-xs font-semibold text-blue-800 mb-1">New Style Name</label>
                        <Input placeholder="e.g., Minimalist" value={newStyle.key} onChange={(e) => setNewStyle(p => ({...p, key: e.target.value}))} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-blue-800 mb-1">Description</label>
                        <TextArea rows={3} placeholder="Describe the visual style..." value={newStyle.value} onChange={(e) => setNewStyle(p => ({...p, value: e.target.value}))} />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button size="small" onClick={() => setIsAdding(false)}>Cancel</Button>
                        <Button size="small" type="primary" onClick={handleAddStyle}>Add Style</Button>
                    </div>
                </div>
            )}
            
            {!isAdding && (
                <button onClick={() => setIsAdding(true)} className="w-full border-2 border-dashed border-slate-300 rounded-lg py-3 text-center text-slate-500 hover:border-blue-500 hover:text-blue-500 transition-colors">
                    <PlusOutlined /> Add a Visual Style
                </button>
            )}
        </div>
    );
};